/**
 * Bill Scan Service — AI-powered recurring bill detection
 */

import { supabase } from '../lib/supabase';
import { billService } from './bill-service';
import type { Bill } from '../types/bill.types';
import type { BillFrequency } from '../types/bill.types';
import {
  candidateToCreateInput,
  type CreateBillsFromScanResponse,
  type DetectedBillCandidate,
  type ScanBillsInput,
  type ScanBillsResponse,
} from '../types/bill-scan.types';

const FREQUENCIES: BillFrequency[] = [
  'weekly',
  'fortnightly',
  'monthly',
  'quarterly',
  'yearly',
  'one_off',
];

function isFrequency(value: unknown): value is BillFrequency {
  return typeof value === 'string' && (FREQUENCIES as string[]).includes(value);
}

function createTempId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `scan-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function normaliseName(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

function mapRawCandidate(raw: Record<string, unknown>): DetectedBillCandidate | null {
  const name = typeof raw.name === 'string' ? raw.name.trim() : '';
  const amount = typeof raw.amount === 'number' ? raw.amount : Number(raw.amount);
  if (!name || !Number.isFinite(amount) || amount <= 0) return null;
  if (!isFrequency(raw.frequency)) return null;
  const dueDate = typeof raw.dueDate === 'string' ? raw.dueDate : '';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dueDate)) return null;

  return {
    id: createTempId(),
    name,
    amount: Math.round(amount * 100) / 100,
    frequency: raw.frequency,
    dueDate,
    ...(typeof raw.dayOfMonth === 'number' ? { dayOfMonth: raw.dayOfMonth } : {}),
    ...(typeof raw.categoryId === 'string' && raw.categoryId
      ? { categoryId: raw.categoryId }
      : {}),
    ...(typeof raw.categoryName === 'string' ? { categoryName: raw.categoryName } : {}),
    ...(typeof raw.confidence === 'number' ? { confidence: raw.confidence } : {}),
    ...(typeof raw.occurrenceCount === 'number'
      ? { occurrenceCount: raw.occurrenceCount }
      : {}),
  };
}

/** Mark candidates that already match existing bills (same name + amount within ±$1). */
export function markExistingBillCandidates(
  candidates: DetectedBillCandidate[],
  existingBills: Bill[]
): DetectedBillCandidate[] {
  return candidates.map((c) => {
    const match = existingBills.some(
      (b) =>
        normaliseName(b.name) === normaliseName(c.name) &&
        Math.abs(b.amount - c.amount) <= 1
    );
    return match ? { ...c, alreadyExists: true } : { ...c, alreadyExists: false };
  });
}

class BillScanService {
  async scanForBills(input: ScanBillsInput): Promise<ScanBillsResponse> {
    if (!input.dateFrom || !input.dateTo) {
      return {
        error: {
          code: 'VALIDATION_ERROR',
          message: 'dateFrom and dateTo are required',
        },
      };
    }

    try {
      const body: Record<string, unknown> = {
        dateFrom: input.dateFrom,
        dateTo: input.dateTo,
      };
      if (input.accountIds && input.accountIds.length > 0) {
        body.accountIds = input.accountIds;
      }

      const { data, error } = await (supabase as any).functions.invoke('ai-scan-bills', {
        body,
      });

      // supabase-js often parks the JSON body on error.context for non-2xx responses
      let payload: Record<string, unknown> | null =
        data && typeof data === 'object' ? (data as Record<string, unknown>) : null;

      if (error) {
        try {
          const ctx = (error as { context?: Response }).context;
          if (ctx && typeof ctx.json === 'function') {
            const bodyJson = await ctx.json();
            if (bodyJson && typeof bodyJson === 'object') {
              payload = bodyJson as Record<string, unknown>;
            }
          }
        } catch {
          // ignore body parse errors
        }

        // If body includes candidates (shouldn't on error, but be defensive), use them
        if (payload && Array.isArray(payload.candidates)) {
          // fall through to mapping below
        } else {
          const msg =
            (typeof payload?.message === 'string' && payload.message) ||
            (typeof payload?.error === 'string' && payload.error) ||
            error.message ||
            'Failed to scan for bills';
          const code =
            payload?.error === 'monthly_limit_reached' || payload?.error === 'MONTHLY_LIMIT_REACHED'
              ? 'MONTHLY_LIMIT_REACHED'
              : payload?.error === 'provider_unavailable' ||
                  String(msg).includes('503') ||
                  String(msg).toLowerCase().includes('unavailable')
                ? 'PROVIDER_UNAVAILABLE'
                : 'AI_ERROR';
          return {
            error: {
              code,
              message: msg,
              ...(code === 'PROVIDER_UNAVAILABLE' ? { retryAfter: 60 } : {}),
            },
          };
        }
      }

      if (payload?.error === 'provider_unavailable') {
        return {
          error: {
            code: 'PROVIDER_UNAVAILABLE',
            message:
              (typeof payload.message === 'string' && payload.message) ||
              'The AI service is temporarily unavailable. Please try again.',
            retryAfter:
              typeof payload.retry_after === 'number' ? payload.retry_after : 60,
          },
        };
      }

      if (payload?.error === 'monthly_limit_reached') {
        return {
          error: {
            code: 'MONTHLY_LIMIT_REACHED',
            message:
              (typeof payload.message === 'string' && payload.message) ||
              'Your monthly AI spend limit has been reached. Adjust it in Settings → AI Configuration.',
          },
        };
      }

      if (payload?.error && !Array.isArray(payload.candidates)) {
        return {
          error: {
            code: 'AI_ERROR',
            message:
              (typeof payload.message === 'string' && payload.message) ||
              (typeof payload.error === 'string' ? payload.error : 'Scan failed'),
          },
        };
      }

      const rawList = Array.isArray(payload?.candidates) ? payload.candidates : [];
      const candidates: DetectedBillCandidate[] = [];
      for (const raw of rawList) {
        if (raw && typeof raw === 'object') {
          const mapped = mapRawCandidate(raw as Record<string, unknown>);
          if (mapped) candidates.push(mapped);
        }
      }

      // Deduplicate against existing bills
      const existing = await billService.listBills();
      const bills = existing.data ?? [];
      const withFlags = markExistingBillCandidates(candidates, bills);

      return { data: withFlags };
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Unknown error';
      return { error: { code: 'UNKNOWN_ERROR', message } };
    }
  }

  async createBillsFromCandidates(
    candidates: DetectedBillCandidate[]
  ): Promise<CreateBillsFromScanResponse> {
    const selected = candidates.filter((c) => !c.alreadyExists);
    if (selected.length === 0) {
      return {
        error: {
          code: 'VALIDATION_ERROR',
          message: 'No bills selected to create',
        },
      };
    }

    let created = 0;
    const failed: Array<{ name: string; message: string }> = [];

    for (const c of selected) {
      const input = candidateToCreateInput(c);
      const result = await billService.createBill(input);
      if (result.error) {
        failed.push({ name: c.name, message: result.error.message });
      } else {
        created += 1;
      }
    }

    return { data: { created, failed } };
  }
}

export const billScanService = new BillScanService();

export async function scanForBills(input: ScanBillsInput): Promise<ScanBillsResponse> {
  return billScanService.scanForBills(input);
}

export async function createBillsFromCandidates(
  candidates: DetectedBillCandidate[]
): Promise<CreateBillsFromScanResponse> {
  return billScanService.createBillsFromCandidates(candidates);
}
